import { Grid2, Button, Chip, IconButton } from "@mui/material";
import { memo, useState, useRef, useEffect } from "react";
import SettingsFormField from "./SettingsFormField";
import { icon } from "@/app/style/icons";

export interface SettingsTagListProps {
    tags: string[];
    onAdd: (tag: string) => void;
    onRemove: (tag: string) => void;
    placeholder?: string;
    label: string;
    loading?: boolean;
    error?: string;
}

export const SettingsTagList = memo(({ tags, onAdd, onRemove, placeholder, label, loading, error }: SettingsTagListProps) => {
  const [newTag, setNewTag] = useState("");
  const [showButtonText, setShowButtonText] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    if (!containerRef.current) return;
    
    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        // If width is below 400px, hide button text, otherwise show it
        setShowButtonText(entry.contentRect.width > 400);
      }
    });
    
    resizeObserver.observe(containerRef.current);
    
    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const onTagChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = (e) => {
      setNewTag(e.target.value);
  }

  const handleAdd = () => {
      if (newTag.trim()) {
          onAdd(newTag.trim());
          setNewTag("");
      }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
          e.preventDefault();
          handleAdd();
      }
  };

  const handleBlur = () => {
      handleAdd();
  };

  return (
      <Grid2 container size={12} spacing={2}>
          <Grid2 size={12} ref={containerRef}>
              <SettingsFormField
                  label={label}
                  value={newTag}
                  onChange={onTagChange}
                  placeholder={placeholder}
                  onKeyDown={handleKeyDown}
                  onBlur={handleBlur}
                  error={error}
                  slotProps={{
                      input: {
                          endAdornment: (
                              <>
                                  <IconButton 
                                      onClick={(e) => {
                                          e.preventDefault();
                                          handleAdd();
                                      }} 
                                      disabled={!newTag.trim()}
                                      size="small"
                                  >
                                      <icon.check />
                                  </IconButton>
                                  {
                                    showButtonText && (
                                        <Button 
                                            variant="text" 
                                            onClick={(e) => {
                                                e.preventDefault();
                                                handleAdd();
                                            }} 
                                            sx={{ 
                                                marginLeft: 1.5, 
                                                marginRight: 1,
                                                minWidth: 0
                                            }}
                                            disabled={!newTag.trim()}
                                        >
                                            Hinzufügen
                                        </Button>
                                    )
                                  }
                              </>
                          )
                      }
                  }}
              />
          </Grid2>
          <Grid2 container size={12} spacing={1}>
              {tags.map((tag) => (
                  <Grid2 key={tag}>
                      <Chip label={tag} onDelete={() => onRemove(tag)} sx={{ color: "semantic.text.paragraph"}} />
                  </Grid2>
              ))}
          </Grid2>
      </Grid2>
  );
});